(() => {
  /* Hard guard: even if accidentally included twice, initialize only once. */
  if (window.__SEELENHAUCH_MAISON_MOTION_V21__) return;
  window.__SEELENHAUCH_MAISON_MOTION_V21__ = true;

  const prepared = new WeakSet();
  const observed = new WeakSet();

  const reduced = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  function revealWhenVisible(el, delay = 0) {
    if (!el || observed.has(el)) return;
    observed.add(el);

    const reveal = () => {
      window.setTimeout(() => el.classList.add('is-visible'), delay);
    };

    if (reduced() || !('IntersectionObserver' in window)) {
      reveal();
      return;
    }

    const rect = el.getBoundingClientRect();
    const alreadyVisible =
      rect.top < window.innerHeight * 0.92 &&
      rect.bottom > window.innerHeight * 0.04;

    if (alreadyVisible) {
      /* Two paints ensure the initial CSS state is visible to the browser. */
      requestAnimationFrame(() => requestAnimationFrame(() => {
        window.setTimeout(() => el.classList.add('is-visible'), 90 + delay);
      }));
      return;
    }

    const io = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal();
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.10,
      rootMargin: '0px 0px -6% 0px'
    });

    io.observe(el);
  }

  /* HERO
     Important: no MutationObserver here.
     v2 used a subtree class observer, which could react to the classes
     added by the motion system itself and create a loop. */
  function prepareHero(wrapper) {
    if (!wrapper || prepared.has(wrapper)) return;
    prepared.add(wrapper);

    const candidates = new Set();

    [
      '.maison-hero__content',
      '.maison-hero-v2__content',
      '.maison-hero-slide__content',
      '.maison-hero-slider__content',
      '[class*="maison-hero"][class*="content"]'
    ].forEach(selector => {
      wrapper.querySelectorAll(selector).forEach(el => candidates.add(el));
    });

    /* Conservative fallback only when no known content wrapper exists. */
    if (!candidates.size) {
      wrapper.querySelectorAll('h1,h2').forEach(heading => {
        let node = heading.parentElement;
        for (let depth = 0; depth < 3 && node && node !== wrapper; depth++, node = node.parentElement) {
          const hasText = node.querySelector('h1,h2,p');
          const hasAction = node.querySelector('a,button');
          const directMedia = node.querySelector(':scope > img, :scope > picture, :scope > video');
          if (hasText && (hasAction || node.querySelector('p')) && !directMedia) {
            candidates.add(node);
            break;
          }
        }
      });
    }

    candidates.forEach(content => {
      if (content.dataset.maisonV21Hero === '1') return;
      content.dataset.maisonV21Hero = '1';

      const items = [];
      const selectors = [
        '[class*="eyebrow"]',
        '[class*="kicker"]',
        '[class*="subtitle"]',
        'h1',
        'h2',
        'p',
        'a[class*="button"]',
        '.button',
        'button'
      ];

      selectors.forEach(selector => {
        content.querySelectorAll(selector).forEach(el => {
          if (items.includes(el)) return;
          if (items.some(parent => parent.contains(el))) return;
          items.push(el);
        });
      });

      if (!items.length) return;

      content.classList.add('maison-v21-hero-content');

      items.slice(0,8).forEach((item, index) => {
        item.classList.add('maison-v21-hero-item');
        item.style.setProperty('--maison-v21-delay', `${index * 85}ms`);
      });

      revealWhenVisible(content, 20);
    });
  }

  /* LOOKBOOK */
  function prepareLookbook(wrapper) {
    if (!wrapper || prepared.has(wrapper)) return;
    prepared.add(wrapper);

    const root =
      wrapper.querySelector('.maison-lookbook') ||
      wrapper.querySelector('[class*="maison-lookbook"]');

    if (!root) return;

    wrapper.classList.add('maison-v21-lookbook-ready');
    revealWhenVisible(wrapper);

    const media = [...wrapper.querySelectorAll(
      '.maison-lookbook__media, .maison-lookbook__image, [class*="lookbook"][class*="image"], [class*="lookbook"][class*="media"]'
    )]
      .filter(el => el.querySelector('img,video,picture') || el.matches('img,video,picture'))
      .slice(0,6);

    [...new Set(media)].forEach((el, index) => {
      el.classList.add('maison-v21-lookbook-media');
      revealWhenVisible(el, 50 + index * 45);
    });

    const hotspots = [...wrapper.querySelectorAll(
      '.maison-lookbook__hotspot, [class*="lookbook"][class*="hotspot"], button[class*="hotspot"]'
    )].slice(0,10);

    hotspots.forEach((el, index) => {
      el.classList.add('maison-v21-hotspot');
      revealWhenVisible(el, 160 + index * 45);
    });
  }

  /* FOOTER */
  function prepareFooter(wrapper) {
    if (!wrapper || prepared.has(wrapper)) return;
    prepared.add(wrapper);

    const footer =
      wrapper.querySelector('.maison-footer') ||
      wrapper.querySelector('footer') ||
      wrapper;

    footer.classList.add('maison-v21-footer-ready');

    let groups = [
      ...footer.querySelectorAll(
        '.maison-footer__grid > *, .maison-footer__columns > *, [class*="footer"][class*="column"], [class*="footer"][class*="block"]'
      )
    ];

    /* If custom footer classes differ, use only direct structural children. */
    if (!groups.length) {
      groups = [...footer.children].filter(el =>
        !['SCRIPT','STYLE','LINK'].includes(el.tagName)
      );
    }

    [...new Set(groups)].slice(0,8).forEach((group, index) => {
      group.classList.add('maison-v21-footer-group');
      group.style.setProperty('--maison-v21-delay', `${index * 70}ms`);
    });

    revealWhenVisible(footer);
  }

  function init(scope = document) {
    const heroWrappers = new Set();
    if (scope.matches?.('.shopify-section[id*="maison_hero"]')) heroWrappers.add(scope);
    scope.querySelectorAll?.('.shopify-section[id*="maison_hero"]').forEach(el => heroWrappers.add(el));
    heroWrappers.forEach(prepareHero);

    const lookbookWrappers = new Set();
    if (scope.matches?.('.shopify-section[id*="maison_lookbook"]')) lookbookWrappers.add(scope);
    scope.querySelectorAll?.('.shopify-section[id*="maison_lookbook"]').forEach(el => lookbookWrappers.add(el));
    scope.querySelectorAll?.('.maison-lookbook').forEach(el => {
      lookbookWrappers.add(el.closest('.shopify-section') || el);
    });
    lookbookWrappers.forEach(prepareLookbook);

    const footerWrappers = new Set();
    if (scope.matches?.('.shopify-section[id*="maison_footer"]')) footerWrappers.add(scope);
    scope.querySelectorAll?.('.shopify-section[id*="maison_footer"]').forEach(el => footerWrappers.add(el));
    scope.querySelectorAll?.('.maison-footer').forEach(el => {
      footerWrappers.add(el.closest('.shopify-section') || el);
    });
    footerWrappers.forEach(prepareFooter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => init(event.target));

  window.maisonMotionV21 = {
    status() {
      return {
        initialized: true,
        heroContents: document.querySelectorAll('.maison-v21-hero-content').length,
        lookbooks: document.querySelectorAll('.maison-v21-lookbook-ready').length,
        footers: document.querySelectorAll('.maison-v21-footer-ready').length
      };
    }
  };
})();
